/**
 * Day 81.5 P2.F2 — extracted from BuyingSession.tsx so chip-row computation
 * can be imported from src/test/day77ChipRow.test.tsx without tripping the
 * react-refresh/only-export-components ESLint rule (Day 66 baseline =
 * 0 errors / 3 warnings, preserved). Mirrors Day 79 P5.4 tierValidation.ts
 * extract pattern.
 *
 * Asymmetric chip design per ADR Decision 44 (Day 77 P5.2): sidebar-sourced
 * chips display-only (no dismiss X); manual chips dismissible. Day 79 P5.4
 * F42 — Tier Mode "paused" chip surfaces when selectedPath='all' with
 * tierMode.enabled=true (otherwise-invisible state).
 *
 * Audit cross-ref: audits/phase-5-completion-audit-2026-05-19.md F2 +
 * BACKLOG L30 routed Day 81.5 per 81b.1 Sol-lock S4 cluster split.
 */

import {
  FILTER_FIELDS,
  CONSTRAINT_FIELDS,
  GUARDRAIL_FIELDS,
} from '../../lib/buyingSessionDefaults';

export interface FilterChip {
  key: string;
  label: string;
  source: 'sidebar' | 'manual';
}

export interface SidebarChipInputs {
  filters: Record<string, string | undefined>;
  constraints: Record<string, string | undefined>;
  // Day 98.1 §10 — optional so the existing computeSidebarChips callers
  // (BuyingSession, day77ChipRow.test) stay tsc-clean until Phase 3 passes it.
  guardrails?: Record<string, string | undefined>;
  /** Active fulfillment-path tab ('all' = cross-path view). */
  selectedPath: string;
  tierModeEnabled: boolean;
}

export interface ManualChipInputs {
  selectedBrand: string | null;
  selectedMfrIds: Set<string>;
  manufacturers: Array<{ id: string; name: string }>;
  onlyBranded: boolean;
  matchedBrandsOnly: boolean;
}

export function computeSidebarChips(inputs: SidebarChipInputs): FilterChip[] {
  const { filters, constraints, guardrails, selectedPath, tierModeEnabled } = inputs;
  const chips: FilterChip[] = [];
  FILTER_FIELDS.forEach((def) => {
    const value = filters[def.key];
    if (value != null && value !== '') {
      chips.push({
        key: `filter:${def.key}`,
        label: `${def.label}: ${value}${def.unit}`,
        source: 'sidebar',
      });
    }
  });
  CONSTRAINT_FIELDS.forEach((def) => {
    const value = constraints[def.key];
    if (value == null || value === '') return;
    if (def.kind === 'boolean') {
      if (value === 'true') {
        chips.push({
          key: `constraint:${def.key}`,
          label: def.label,
          source: 'sidebar',
        });
      }
    } else {
      chips.push({
        key: `constraint:${def.key}`,
        label: `${def.label}: ${value}${def.unit}`,
        source: 'sidebar',
      });
    }
  });
  // Day 98.1 §10 — advisory guardrail chips (constraints-sibling parity; ADR-44
  // sidebar-sourced → display-only). All 9 numeric → the constraint else-branch
  // shape; the $-unit guardrail keeps the existing suffix-$ quirk ("…: 500$",
  // same as max_spend today) — bundled with the deferred fmt$ formatter pass.
  GUARDRAIL_FIELDS.forEach((def) => {
    const value = guardrails?.[def.key];
    if (value == null || value === '') return;
    chips.push({
      key: `guardrail:${def.key}`,
      label: `${def.label}: ${value}${def.unit}`,
      source: 'sidebar',
    });
  });
  // Day 79 P5.4 F42 — Tier Mode paused indicator (Q1 (b) chip-persist).
  // When the path tab switches to 'all', tierMode.enabled persists but the
  // sidebar toggle disables + the RPC short-circuits + tier columns drop.
  // The chip surfaces the otherwise-invisible "paused" state so single-
  // channel switch-back resumes seamlessly without losing configured tiers.
  if (selectedPath === 'all' && tierModeEnabled) {
    chips.push({
      key: 'tier-mode-paused',
      label: 'Tier Mode (paused — single channel only)',
      source: 'sidebar',
    });
  }
  return chips;
}

export function computeManualChips(inputs: ManualChipInputs): FilterChip[] {
  const { selectedBrand, selectedMfrIds, manufacturers, onlyBranded, matchedBrandsOnly } = inputs;
  const chips: FilterChip[] = [];
  if (selectedBrand) {
    chips.push({ key: 'brand', label: `Brand: ${selectedBrand}`, source: 'manual' });
  }
  if (selectedMfrIds.size > 0) {
    let label: string;
    if (selectedMfrIds.size === 1) {
      const id = [...selectedMfrIds][0];
      const name = manufacturers.find((m) => m.id === id)?.name ?? id;
      label = `Manufacturer: ${name}`;
    } else {
      label = `Manufacturer (${selectedMfrIds.size})`;
    }
    chips.push({ key: 'manufacturer', label, source: 'manual' });
  }
  // Q4 collapse — onlyBranded and matchedBrandsOnly are mutually exclusive
  // at the toolbar 3-button strip; one chip slot with conditional label.
  if (onlyBranded) {
    chips.push({ key: 'filter-mode', label: 'Only Branded', source: 'manual' });
  } else if (matchedBrandsOnly) {
    chips.push({ key: 'filter-mode', label: 'Matched Brands Only', source: 'manual' });
  }
  return chips;
}
